// BluprintJs
import { Button, Card, Intent } from "@blueprintjs/core"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import CustomSpinner from "components/Atoms/CustomSpinner"
// Form Components
import StringInput from "components/Atoms/StringInput"
import Toggle from "components/Atoms/Toggle"
import Error from "components/Common/Error"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FormController from "components/Molecules/FormController"
import MutationOptions from "components/Organisms/MutationOptions"
import ROUTES from "constants/routes.constants"
import { useDockDetailsLazyQuery } from "generated/graphql"
import { ADD_UPDATE_DOCK } from "graphql/dock/dock.mutations.gql"
import { DEPOTS, DISTRIBUTION_CENTERS } from "graphql/selector.queries.gql"
import { useFormDisabled } from "hooks/useAuth"
import { getErrors } from "hooks/useErrors"
import { cloneDeep, set } from "lodash-es"
import dockModel from "models/atoms/dock.model"
import React, { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import AppToaster from "utils/AppToaster"
export default function DockRegistration() {
  const { id } = useParams()
  const idNumber = parseInt(id)
  const navigate = useNavigate()
  const [state, setState] = useState<any>({})
  const [getDetails, { error, loading }] = useDockDetailsLazyQuery({
    onCompleted({ dockDetail }) {
      setState(dockDetail)
    },
  })
  useEffect(() => {
    if (idNumber) {
      getDetails({ variables: { id: idNumber.toString() } })
    } else {
      setState({})
    }
    return function cleanup() {
      setState({})
    }
  }, [idNumber, getDetails])

  function handleChange(path, value) {
    const stateCopy = cloneDeep(state)
    set(stateCopy, path, value)
    setState(stateCopy)
  }
  function showToast({ addUpdateDock }) {
    AppToaster.show({
      message: `Dock ${addUpdateDock.dock.name} ${
        addUpdateDock.dock.id ? "Edited" : "Saved"
      } successfully.`,
      intent: Intent.SUCCESS,
    })
  }

  const parsedVariables = useMemo(
    () => ({
      ...state,
      distributionCentreId: state?.distributionCentre?.id,
      depotId: state?.depot?.id,
    }),
    [state]
  )

  const errors = useMemo(() => {
    return getErrors({ schema: dockModel, state: state })
  }, [state])

  const disabled = useFormDisabled("dock", parsedVariables)
  if (loading) return <CustomSpinner />
  if (error) return <Error message={error.message} />
  return (
    <Card>
      <div className="mt-2 w-full flex justify-between">
        <span>
          <Button
            icon="arrow-left"
            onClick={() => {
              navigate(-1)
            }}
            minimal={true}
          ></Button>
          <h5 className="ml-3" style={{ display: "inline-block" }}>
            {id ? "Edit" : "New"} Dock
          </h5>
        </span>
      </div>

      <div className="container mt-3">
        <FormController
          source={state}
          errors={errors.details}
          onChange={handleChange}
          disabled={disabled}
          disableAll={true}
        >
          <div className="grid grid-cols-4 gap-4">
            <StringInput
              autoFocus={true}
              label="Dock Name"
              helperText="eg. Dock 1"
              path="name"
            />
            <StringInput
              label="Abbreviation"
              helperText="eg. DCK1"
              path="codename"
            />
            <DynamicSelector
              big={true}
              query={DISTRIBUTION_CENTERS}
              path="distributionCentre"
              label="Distribution Center"
            />
            <DynamicSelector
              label="Depot"
              big={true}
              query={DEPOTS}
              path="depot"
            />
            <CustomFormGroup label="Archive" big={true}>
              <Toggle
                large
                checked={state?.archive}
                onChange={() => {
                  setState(prev => ({
                    ...prev,
                    archive: !prev.archive,
                  }))
                }}
              />
            </CustomFormGroup>
          </div>
        </FormController>

        <div className="flex justify-end">
          <MutationOptions
            disabled={disabled}
            mutation={ADD_UPDATE_DOCK}
            variables={parsedVariables}
            onError={error => {
              AppToaster.show({
                message: "Unable to save dock",
                intent: Intent.DANGER,
              })
            }}
            onSave={data => {
              setState({})
              showToast(data)
              if (!idNumber) {
                navigate(`${ROUTES.docks}?update=${Date.now()}`, {
                  replace: true,
                })
              }
            }}
            onSaveAndAdd={data => {
              showToast(data)
              setState({})
            }}
            onSaveAndEdit={data => {
              const entityId = data?.addUpdateDock?.dock?.id
              showToast(data)
              if (!parseInt(id))
                navigate(ROUTES.dockDetail(entityId), { replace: true })
            }}
          ></MutationOptions>
        </div>
      </div>
    </Card>
  )
}
